import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { ApiRequestError } from '@/shared/api/api-error';

import { loadField, sendFieldCommand } from './field-client';

import type { FieldCommand, FieldSnapshot } from '@mio/shared';

export const FIELD_QUERY_KEY = ['field', 'current'] as const;

export function useField() {
  const client = useQueryClient();
  return useQuery({
    queryKey: FIELD_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const next = await loadField(signal);
      const current = client.getQueryData<FieldSnapshot>(FIELD_QUERY_KEY);
      // A poll can finish after a command has already published a newer snapshot.
      return current?.engagement.id === next.engagement.id &&
        current.engagement.revision > next.engagement.revision
        ? current
        : next;
    },
    retry: 1,
    refetchOnWindowFocus: true,
    refetchInterval: (query) =>
      query.state.data?.operations.some(
        (operation) => operation.status === 'pending' || operation.status === 'running',
      )
        ? 1800
        : 15000,
  });
}

export function useFieldCommand(snapshot: FieldSnapshot | undefined) {
  const client = useQueryClient();
  const unresolved = useRef<{ fingerprint: string; command: FieldCommand } | null>(null);
  const busy = useRef(false);
  const mutation = useMutation({
    mutationFn: sendFieldCommand,
    onSuccess: (next) => {
      client.setQueryData<FieldSnapshot>(FIELD_QUERY_KEY, (current) =>
        current?.engagement.id === next.engagement.id &&
        current.engagement.revision > next.engagement.revision
          ? current
          : next,
      );
      unresolved.current = null;
    },
    onError: (error) => {
      // A rejected command was not committed; retry against refreshed state.
      if (error instanceof ApiRequestError && error.status < 500) unresolved.current = null;
      void client.invalidateQueries({ queryKey: FIELD_QUERY_KEY });
    },
  });
  return {
    ...mutation,
    execute: async (action: FieldCommand['action']) => {
      if (!snapshot) throw new Error('Workbench is still loading.');
      if (busy.current) throw new Error('Please wait for your current action to finish.');
      const fingerprint = JSON.stringify(action);
      const command =
        unresolved.current?.fingerprint === fingerprint
          ? unresolved.current.command
          : {
              requestId: crypto.randomUUID(),
              expectedRevision: snapshot.engagement.revision,
              action,
            };
      unresolved.current = { fingerprint, command };
      busy.current = true;
      try {
        return await mutation.mutateAsync(command);
      } finally {
        busy.current = false;
      }
    },
  };
}
